<template>
    <div id="whatsappcontactlist">
        <div class="my-1 wp-list-searching-input">
            <b-button @click="refresh" class="m-2">{{ $t("message.refresh") }}</b-button>
            <a-input-search
                v-model="searchText"
                @input="onSearch($event.target.value)"
                :placeholder="$t('message.search')"
                class="search-input"
            />
            <div class="mr-5">
                <a-button @click="showModal">
                    {{ $t("wp.startChatWithAnyNo") }}
                </a-button>
                <a-modal
                    :title="$t('wp.typeInANumberDesc')"
                    :visible="visible"
                    :confirm-loading="confirmLoading"
                    @ok="handleOk(wpContactNumber)"
                    @cancel="handleCancel"
                    class="row d-flex flex-row"
                >
                    <div v-if="showAlertMessage" class="pb-3">
                        <a-alert :message="$t('wp.toLittleChar')" type="error" banner />
                    </div>
                    <div class="d-flex flex-row align-items-center justify-content-center">
                        <vue-country-code
                            class="col-3 country-code"
                            :enabledCountryCode="true"
                            @onSelect="onSelect($event)"
                            :defaultCountry="userLastCountry"
                        />
                        <div class="col-8">
                            <a-input
                                v-model="wpContactNumber"
                                :placeholder="$t('message.phone')"
                                autocomplete="off"
                                trim
                                type="text"
                                v-mask="'##########'"
                            />
                        </div>
                    </div>
                </a-modal>
            </div>
        </div>
        <a-table
            :columns="cols"
            :dataSource="getOnlyPersonelContacts"
            :pagination="pagination"
            :loading="loading"
            @change="handleTableChange"
            :key="'vtable-' + renderCount"
            :locale="{
                emptyText: $t('message.noContactFound'),
            }"
            rowKey="id"
            size="small"
            class="custom-table"
        >
            <div slot="avatarSlot" slot-scope="data, item" class="p-1">
                <a-avatar
                    :size="30"
                    shape="square"
                    style="background-color: #3899ff; border-radius: 7px !important"
                    :username="item.name"
                    :src="$_.get(item, 'profilePicThumbObj.eurl')"
                >
                    {{ item.name.substr(0, 1).toLocaleUpperCase() }}
                </a-avatar>
            </div>

            <div slot="action" slot-scope="data, item">
                <div class="d-flex justify-content-end" style="margin-right: -10px !important">
                    <md-button
                        id="joinChat"
                        v-tippy="{
                            followCursor: true,
                            placement: 'bottom-end',
                            theme: isDarkMode ? 'light-border' : 'google',
                        }"
                        :content="$t('wp.startChatWp')"
                        data-v-step="16"
                        @click="startChat(item.id)"
                    >
                        {{ $t("wp.startChatWp") }}
                        <md-icon style="color: #06e576; font-size: 12px"> whatsapp</md-icon>
                    </md-button>
                </div>
            </div>
        </a-table>
    </div>
</template>

<script>
import { log2 } from "../utils/log2";
import { sendSocketMessage, socketMessageStream } from "../utils/socketStream";
import { mapGetters, mapMutations, mapState } from "vuex";
import { tableSorter } from "../utils/utils";
import Fuse from "fuse.js";
import { filter, first, timeout } from "rxjs/operators";
import Vue from "vue";
import VueCountryCode from "vue-country-code";
import VueTheMask from "vue-the-mask";
import _ from "lodash";
import ChatWatcher from "../ChatWatcherV2";

Vue.use(VueTheMask);
Vue.use(VueCountryCode);

const log = log2("WhatsappContactList");
export default {
    name: "WhatsappContactList",
    components: { VueCountryCode },
    props: {},
    data() {
        return {
            visible: false,
            confirmLoading: false,
            internationalCallingCode: "90",
            wpContactNumber: "",
            showAlertMessage: false,
            lastPersonelContacts: [],
            searchText: "",
            renderCount: 0,
            currentSort: {
                columnKey: "connectionTime",
                order: "ascend",
            },
            pagination: {
                current: 1,
                total: 0,
                defaultPageSize: 15,
                hideOnSinglePage: true,
                pageSizeOptions: ["10", "15", "20", "50", "100"],
                showQuickJumper: true,
                showSizeChanger: true,
            },
            cols: [
                {
                    key: "profilePicThumbObj",
                    dataIndex: "profilePicThumbObj.eurl",
                    title: this.$t("message.profile"),
                    ...tableSorter("profilePicThumbObj", "string", this.currentSort),
                    scopedSlots: { customRender: "avatarSlot" },
                },
                {
                    key: "name",
                    dataIndex: "name",
                    title: this.$t("message.name"),
                    ...tableSorter("name", "string", this.currentSort),
                    scopedSlots: { customRender: "name" },
                },
                {
                    key: "id",
                    dataIndex: `id`,
                    title: this.$t("message.number"),
                    ...tableSorter("id", "string", this.currentSort),
                    scopedSlots: { customRender: "id" },
                },
                {
                    title: this.$t("wp.action"),
                    key: "operation",
                    scopedSlots: { customRender: "action" },
                    width: "100px",
                },
            ],
            loading: true,
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            userLastCountry: (state) => _.get(state.auth.user, "lastGeoLocation.country"),
            contacts: (state) => state.whatsapp.contacts,
            searchingResultsOfWpContact: (state) => state.whatsapp.searchingResultsOfWpContact,
            totalContacts: (state) => state.whatsapp.total,
            whatsappPlusActive: (state) => _.get(state.setting.settings, "whatsapp.plus.enabled", false),
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
            myActiveConversations: "chat/getMyActiveConversations",
        }),
        getOnlyPersonelContacts() {
            log("getOnlyPersonelContacts running");
            if (!!this.contacts) {
                setTimeout(() => {
                    this.loading = false;
                }, 500);
                let tempContacts = this.contacts;
                let onlyPersonelContacts = tempContacts.filter((x) => x.id.includes("@c") && !!x.name);
                this.lastPersonelContacts = onlyPersonelContacts;
                for (let i = 0; i < onlyPersonelContacts.length; i++) {
                    this.lastPersonelContacts[i].id = onlyPersonelContacts[i].id.slice(0, -5);
                }
                this.lastPersonelContacts = this.getFilteredRows(this.lastPersonelContacts);
                return this.lastPersonelContacts;
            } else {
                this.loading = false;
                return;
            }
        },
    },
    mounted() {
        this.pagination.total = this.totalContacts;
        this.sendGetContactMessage();
    },
    watch: {},
    methods: {
        ...mapMutations({
            setSearchingResultWPContact: "whatsapp/setSearchingResultWPContact",
        }),
        sendGetContactMessage(refresh = false) {
            const searchQuery = this.searchText !== "" ? this.searchText : null;
            sendSocketMessage("whatsapp get-contacts", {
                siteId: this.site._id,
                page: this.pagination.current,
                perPage: this.pagination.pageSize || this.pagination.defaultPageSize || 10,
                searching: searchQuery,
                refresh,
            });
        },
        startChat(whatsappNumber) {
            ChatWatcher.startChatImmediately({
                whatsappNumber: whatsappNumber + "@c.us",
            });
        },
        refresh: _.debounce(function () {
            this.loading = true;
            setTimeout(() => {
                this.loading = false;
            }, 500);
            this.pagination.current = 1;
            this.sendGetContactMessage(true);
        }, 400),
        handleTableChange(pagination, filters, sorter) {
            this.loading = true;
            setTimeout(() => {
                this.loading = false;
            }, 370);
            this.pagination.current = pagination.current;
            this.pagination.pageSize = pagination.pageSize;
            this.sendGetContactMessage();
        },
        getFilteredRows(getContacts = this.contacts) {
            let fuzzySearch = new Fuse(getContacts, { keys: ["name", "id"] });
            const filteredRows =
                this.searchText.length > 0 ? fuzzySearch.search(this.searchText).map((x) => x.item) : getContacts;
            log("filteredRow", filteredRows);
            return filteredRows;
        },
        onSearch(value) {
            log("onSearch", value);
            this.searchText = value;
            this.refresh();
        },
        onSelect(value) {
            log("onSelect", value);
            this.internationalCallingCode = value.dialCode;
        },
        showModal() {
            this.visible = true;
        },
        handleOk(number) {
            this.wpContactNumber = number.replace(/[{()}]/g, "");
            const contactNumber = this.internationalCallingCode + this.wpContactNumber;
            if (contactNumber.length < 6) {
                this.showAlertMessage = true;
                return;
            }
            this.confirmLoading = true;
            this.showAlertMessage = false;
            this.visible = false;
            this.confirmLoading = false;

            const searchedContacts = JSON.parse(JSON.stringify(this.searchingResultsOfWpContact));
            const isNumberExistInSearchHistory = searchedContacts[number];
            if (isNumberExistInSearchHistory !== undefined) {
                this.readyToChatStartingControl(isNumberExistInSearchHistory);
            } else {
                socketMessageStream
                    .pipe(
                        filter((x) => x.cmd === "whatsapp_contacts_is_includes_number"),
                        timeout(5000),
                        first()
                    )
                    .toPromise()
                    .then((x) => {
                        let searchingResultWPContact = {};
                        const isExist = _.get(x, "data.contactIsExist") || false;
                        searchingResultWPContact[`${number}`] = isExist;
                        this.readyToChatStartingControl(isExist);
                        this.setSearchingResultWPContact(searchingResultWPContact);
                    })
                    .catch((err) => {
                        this.$message.error(this.$t("wp.chatNotStarted"));
                    });

                sendSocketMessage("whatsapp number-is-exist-in-contacts", {
                    siteId: this.site._id,
                    searchingPhoneNumber: contactNumber,
                });
            }
        },
        readyToChatStartingControl(isExist) {
            if (isExist) {
                this.startChat(this.internationalCallingCode + this.wpContactNumber);
            } else if (this.whatsappPlusActive) {
                this.startChat(this.internationalCallingCode + this.wpContactNumber);
            } else {
                this.controlWhatsappPlusIntegration();
            }
        },
        handleCancel(e) {
            this.visible = false;
            this.showAlertMessage = false;
        },
        openNotificationWithIcon(featureType, type = "warning") {
            const messages = {
                wpPlusAndLicenceRequired: {
                    message: this.$t("wp.wpPlusAndLicenceRequired"),
                    description: `${this.$t("wp.wpPlusAndLicenceRequiredDesc")} `,
                },
            };
            const message = messages[featureType];
            const key = `plan`;
            this.$notification[type]({
                message: message.message,
                description: message.description,
                placement: "topRight",
                onClose: () => this.$router.push("/chat?tab=whatsappContacts"),
                key,
                btn: (h) => {
                    let buttonText = !this.whatsappPlusActive
                        ? this.$t("message.changePlan")
                        : this.$t("message.close");
                    return h(
                        "a-button",
                        {
                            props: {
                                type: "primary",
                                size: "small",
                            },
                            on: {
                                click: () => {
                                    if (featureType === "wpPlusAndLicenceRequired") this.$router.push("/settings/billing");

                                    this.$notification.close(key);
                                },
                            },
                        },
                        buttonText
                    );
                },
            });
        },
        controlWhatsappPlusIntegration() {
            if (!this.whatsappPlusActive) {
                this.openNotificationWithIcon("wpPlusAndLicenceRequired");
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.custom-button {
    max-width: 18px;
    min-width: 18px;
    max-height: 18px;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
}

.custom-button svg {
    width: 14px;
    height: 14px;
}

.custom-table {
    background-color: #ffffff;
    border-radius: 6px;
    padding: 10px 20px;
    overflow-y: auto;
}

#joinChat {
    font-size: 12px;
    border-radius: 4px;
    border: solid 1px #92e317;
    background-color: white;

    &:hover {
        border: solid 2px #92e317;
    }
}

.wp-list-searching-input {
    margin: 5px 5px !important;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    text-align: center;
}

.inputs {
    border: 1px solid #e5e5e5;
    position: relative;
    border-radius: 6px;
    -webkit-box-shadow: 0 0 1px #00000080;
    box-shadow: 0 0 1px #00000080;
}
</style>
